"use client";

import { useMemo, useCallback } from "react";
import { CEDE_CEXS, FEATURE_FLAGS, METRICS_EVENTS } from "@cede/types";
import { DISCORD_INVITE_URL, TELEGRAM_URL, TWITTER_URL } from "@cede/utils";
import { type SidebarNavSection, type SidebarNavItem, type SidebarNavDropdownItem } from "./SidebarNav.types";
import { SidebarNavSections } from "./SidebarNavItems";
import {
  CedeHubLogoL,
  CedeLogo,
  DiscordIcon,
  ExchangeIcon,
  TelegramIcon,
  WheelSpinIcon,
  XIcon,
} from "@/components/icons";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
  SidebarState,
} from "@/components/ui/sidebar";
import { ROUTES } from "@/constants/routes";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useFeatureFlagIsOn } from "@/hooks/feature-flags";
import { useNewFeatureBadge } from "@/hooks/ui/useNewFeatureBadge";
import { cn } from "@/lib/utils";
import { useBridgeModalStore } from "@/store/bridgeModalStore";
import { useSpinningWheelDialogStore } from "@/store/spinningWheelDialogStore";
import {
  ArrowLeftRight,
  BookOpen,
  Briefcase,
  CandlestickChart,
  Coins,
  MoreHorizontal,
  ArrowUpDown,
  Ticket,
  MessageCircle,
  TrendingUp,
  Swords,
  PanelLeftIcon,
} from "lucide-react";
import dynamic from "next/dynamic";
import { usePathname, useRouter } from "next/navigation";

const DotLottieReact = dynamic(() => import("@lottiefiles/dotlottie-react").then((mod) => mod.DotLottieReact), {
  ssr: false,
});

export const SidebarNav = () => {
  const router = useRouter();
  const pathname = usePathname();
  const { state, toggleSidebar } = useSidebar();
  const collapsed = state === SidebarState.COLLAPSED;

  const { handleOpen: openBridgeModal } = useBridgeModalStore();
  const { handleOpen: openWheel } = useSpinningWheelDialogStore();
  const { trackEvent } = useMetricsEvents();

  const isQuestEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.QUESTS_HUB_ENABLE);
  const isHteEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.HTE_HUB_ENABLE);
  const showSpinningWheel = useFeatureFlagIsOn(FEATURE_FLAGS.SPINNING_WHEEL_HUB_ENABLE);
  const isLotteryEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.LOTTERY_HUB_ENABLE);
  const isPredictionsEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.PREDICTIONS_HUB_ENABLE);
  const isKrakenPageEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.KRAKEN_PAGE_HUB_ENABLE);
  const isCoinbasePageEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.COINBASE_PAGE_HUB_ENABLE);
  const isLbankPageEnabled = useFeatureFlagIsOn(FEATURE_FLAGS.LBANK_PAGE_HUB_ENABLE);
  const disableLottieAnimations = useFeatureFlagIsOn(FEATURE_FLAGS.PERFORMANCE_HUB_DISABLE_LOTTIE);

  const { showBadge: showTradeNewBadge } = useNewFeatureBadge({
    storageKey: "hasVisitedUnifiedTrade",
    triggerPath: ROUTES.TRADE,
  });
  const { showBadge: showPortfolioNewBadge } = useNewFeatureBadge({
    storageKey: "hasVisitedPortfolio",
    triggerPath: ROUTES.PORTFOLIO,
  });
  const { showBadge: showCedeArenaNewBadge } = useNewFeatureBadge({
    storageKey: "hasVisitedCedeArena",
    triggerPath: ROUTES.CEDE_ARENA,
  });

  const handleDocs = useCallback(() => {
    trackEvent(METRICS_EVENTS.OPEN_EXTERNAL_DOC, {});
    window.open("https://docs.cedehub.io/", "_blank", "noopener,noreferrer");
  }, [trackEvent]);

  const handleBridge = useCallback(() => {
    trackEvent(METRICS_EVENTS.BRIDGE_MODAL_OPENED, { source: "sidebar" });
    openBridgeModal();
  }, [openBridgeModal, trackEvent]);

  const handleWheel = useCallback(() => {
    trackEvent(METRICS_EVENTS.WHEEL_OPEN_FROM_HEADER, { source: "sidebar" });
    openWheel();
  }, [openWheel, trackEvent]);

  const handleLogoClick = useCallback(() => {
    trackEvent(METRICS_EVENTS.NAVIGATION_SIDEBAR_ITEM_CLICKED, {
      label: "Logo",
      href: "/",
      source: "sidebar",
      type: "link",
    });
    router.push(ROUTES.HOME);
  }, [router, trackEvent]);

  const navSections: SidebarNavSection[] = useMemo(
    () => [
      {
        label: "Earn",
        items: [
          {
            label: "Trade & Earn",
            icon: ArrowLeftRight,
            href: ROUTES.HOME,
            subItems: [
              {
                label: "Kraken",
                iconRender: ({ active }) => (
                  <ExchangeIcon
                    exchange={CEDE_CEXS.KRAKEN}
                    size="sm"
                    className={active ? "text-[#7434f3]" : "text-cede-text-secondary"}
                  />
                ),
                href: ROUTES.KRAKEN,
                hoverClassName: "hover:bg-[#5841D8]/15 hover:text-[#b494e6]",
                activeClassName:
                  "data-[active=true]:bg-[#5841D8]/25 data-[active=true]:shadow-[0_0_0_1px_rgba(88,65,216,0.45)]",
                hidden: !isKrakenPageEnabled,
              },
              {
                label: "Coinbase",
                iconRender: ({ active }) => (
                  <ExchangeIcon
                    exchange={CEDE_CEXS.COINBASE}
                    size="sm"
                    className={active ? "text-[#0251FF]" : "text-cede-text-secondary"}
                  />
                ),
                href: ROUTES.COINBASE,
                hoverClassName: "hover:bg-[#0251FF]/15 hover:text-[#4D8AFF]",
                activeClassName:
                  "data-[active=true]:bg-[#0251FF]/25 data-[active=true]:shadow-[0_0_0_1px_rgba(2,81,255,0.45)]",
                hidden: !isCoinbasePageEnabled,
              },
              {
                label: "LBank",
                iconRender: ({ active }) => (
                  <ExchangeIcon
                    exchange={CEDE_CEXS.LBANK}
                    size="sm"
                    className={active ? "text-[#FFDC00]" : "text-cede-text-secondary"}
                  />
                ),
                href: ROUTES.LBANK,
                hoverClassName: "hover:bg-[#FFDC00]/15 hover:text-[#FFE55C]",
                activeClassName:
                  "data-[active=true]:bg-[#FFDC00]/25 data-[active=true]:shadow-[0_0_0_1px_rgba(255,220,0,0.45)]",
                hidden: !isLbankPageEnabled,
              },
            ],
          },
          {
            label: "Cede Arena",
            icon: Swords,
            href: ROUTES.CEDE_ARENA,
            badge: showCedeArenaNewBadge,
            hoverClassName: "hover:bg-medal-gold/10 hover:text-medal-gold",
            activeClassName:
              "data-[active=true]:bg-medal-gold/15 data-[active=true]:text-medal-gold data-[active=true]:shadow-[0_0_0_1px_color-mix(in_srgb,var(--color-medal-gold)_35%,transparent)]",
          },
          { label: "Hold & Earn", icon: Coins, href: ROUTES.HOLD, hidden: !isHteEnabled },
        ],
      },
      {
        label: "Trade",
        items: [
          { label: "Trade", icon: CandlestickChart, href: ROUTES.TRADE, badge: showTradeNewBadge },
          { label: "Predictions", icon: TrendingUp, href: ROUTES.PREDICTIONS, hidden: !isPredictionsEnabled },
        ],
      },
      {
        label: "Compete",
        items: [
          {
            label: "Quests",
            iconRender: ({ active }) => (
              <WheelSpinIcon width={20} height={20} fill={active ? "#5EA6FF" : "#8A8F98"} className="shrink-0" />
            ),
            href: ROUTES.QUESTS,
            hidden: !isQuestEnabled,
          },
          {
            label: "Spin Wheel",
            iconRender: disableLottieAnimations
              ? undefined
              : () => (
                  <div className="h-5 w-5 flex items-center justify-center shrink-0">
                    <DotLottieReact src="/lottie/wheel.lottie" loop autoplay />
                  </div>
                ),
            icon: disableLottieAnimations ? WheelSpinIcon : undefined,
            onClick: handleWheel,
            hidden: !showSpinningWheel,
          },
          { label: "Lottery", icon: Ticket, href: ROUTES.LOTTERY, hidden: !isLotteryEnabled },
        ],
      },
      {
        label: "Assets",
        items: [
          { label: "Portfolio", icon: Briefcase, href: ROUTES.PORTFOLIO, badge: showPortfolioNewBadge },
          { label: "Bridge", icon: ArrowUpDown, onClick: handleBridge },
        ],
      },
      {
        label: "Help",
        items: [
          { label: "Docs", icon: BookOpen, onClick: handleDocs },
          {
            label: "Contact",
            icon: MessageCircle,
            dropdownSide: "right",
            dropdownItems: [
              {
                label: "Discord",
                icon: DiscordIcon,
                onClick: () => window.open(DISCORD_INVITE_URL, "_blank", "noopener,noreferrer"),
              },
              {
                label: "X (Twitter)",
                icon: XIcon,
                onClick: () => window.open(TWITTER_URL, "_blank", "noopener,noreferrer"),
              },
              {
                label: "Telegram",
                icon: TelegramIcon,
                onClick: () => window.open(TELEGRAM_URL, "_blank", "noopener,noreferrer"),
              },
            ],
          },
          {
            label: "Other",
            icon: MoreHorizontal,
            dropdownSide: "right",
            dropdownItems: [
              { label: "Privacy Policy", onClick: () => router.push(ROUTES.PRIVACY_POLICY) },
              { label: "Terms & Conditions", onClick: () => router.push(ROUTES.TERMS_AND_CONDITIONS) },
              { label: "Cookies", onClick: () => router.push(ROUTES.COOKIES) },
            ],
          },
        ],
      },
    ],
    [
      handleBridge,
      handleDocs,
      handleWheel,
      showCedeArenaNewBadge,
      isCoinbasePageEnabled,
      isHteEnabled,
      isKrakenPageEnabled,
      isLotteryEnabled,
      isPredictionsEnabled,
      isQuestEnabled,
      showPortfolioNewBadge,
      showSpinningWheel,
      showTradeNewBadge,
      disableLottieAnimations,
      router,
    ],
  );

  const handleNavigate = useCallback(
    (item: SidebarNavItem | SidebarNavDropdownItem) => {
      if (item.disabled) return;
      trackEvent(METRICS_EVENTS.NAVIGATION_SIDEBAR_ITEM_CLICKED, {
        label: item.label,
        href: item.href ?? null,
        source: "sidebar",
        type: "dropdownItems" in item ? "parent" : "link",
      });
      if (item.onClick) {
        item.onClick();
        return;
      }
      if (item.href) {
        if (item.href.startsWith("http")) {
          window.open(item.href, "_blank", "noopener,noreferrer");
        } else {
          router.push(item.href);
        }
      }
    },
    [router, trackEvent],
  );

  const visibleSections = navSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !item.hidden),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <Sidebar
      collapsible="icon"
      className="border-r border-cede-border-container bg-cede-bg-primary/90 backdrop-blur-md"
    >
      {/* Header with Logo */}
      <SidebarHeader className="border-b border-cede-border-container h-11 flex items-center">
        {collapsed ? (
          <button
            onClick={handleLogoClick}
            className="flex items-center justify-center w-full h-full cursor-pointer hover:opacity-80 transition-opacity"
            aria-label="Go to home page"
          >
            <CedeLogo width={28} height={28} />
          </button>
        ) : (
          <div className="flex items-center h-full px-3">
            <button
              onClick={handleLogoClick}
              className="cursor-pointer hover:opacity-80 transition-opacity"
              aria-label="Go to home page"
            >
              <CedeHubLogoL />
            </button>
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className="overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-cede-border/40 scrollbar-track-transparent hover:scrollbar-thumb-cede-border/60">
        <SidebarNavSections
          sections={visibleSections}
          pathname={pathname}
          collapsed={collapsed}
          onNavigate={handleNavigate}
        />
      </SidebarContent>

      {/* Footer - collapse button with label when expanded */}
      <SidebarFooter className={cn("border-t border-cede-border-container p-1", collapsed && "p-0")}>
        <SidebarMenu className={cn("p-1", collapsed && "p-0.5")}>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={toggleSidebar}
              className={cn("h-8", collapsed && "justify-center")}
              tooltip={collapsed ? "Expand sidebar" : "Hide sidebar"}
            >
              <PanelLeftIcon className={cn("h-4 w-4", collapsed && "rotate-180")} />
              <span>Hide sidebar</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
};
